import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Truck, User, FileText, Eye, ScanLine, CreditCard, IdCard, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPE_CONFIG = {
  carte_grise: { label: 'Carte Grise', icon: FileText, color: 'amber' },
  permis_conduire: { label: 'Permis de conduire', icon: CreditCard, color: 'blue' },
  piece_identite: { label: "Piece d'identite", icon: IdCard, color: 'violet' },
};

const DocCard = ({ doc }) => {
  const config = DOC_TYPE_CONFIG[doc.type] || DOC_TYPE_CONFIG.carte_grise;
  const Icon = config.icon;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-white hover:shadow-sm transition-shadow" data-testid={`ocr-doc-${doc.type}-${doc.side}`}>
      <div className={`w-9 h-9 rounded-lg bg-${config.color}-100 flex items-center justify-center shrink-0`}>
        <Icon className={`w-4 h-4 text-${config.color}-600`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{config.label} — {doc.side}</p>
        <p className="text-xs text-gray-500">{doc.uploadedByName || 'Inconnu'} · {new Date(doc.createdAt).toLocaleDateString('fr-FR')}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {doc.extractionStatus === 'success' && (
          <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700">OCR</Badge>
        )}
        <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.fileUrl}`} target="_blank" rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800" title="Voir">
          <Eye className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

const EmptyState = ({ label }) => (
  <div className="flex flex-col items-center gap-1.5 py-6 text-gray-400">
    <ScanLine className="w-6 h-6" />
    <p className="text-xs">{label}</p>
  </div>
);

const OperationOCRDocuments = ({ operationId }) => {
  const [vehicleDocs, setVehicleDocs] = useState([]);
  const [driverDocs, setDriverDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!operationId) return;
    const fetchDocs = async () => {
      try {
        const res = await axios.get(`${API}/ocr/documents/operation/${operationId}`, {});
        setVehicleDocs(res.data.vehicleDocuments || []);
        setDriverDocs(res.data.driverDocuments || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchDocs();
  }, [operationId]);

  if (loading) return <div className="text-center py-8 text-sm text-gray-400">Chargement...</div>;

  const hasAny = vehicleDocs.length > 0 || driverDocs.length > 0;

  if (!hasAny) {
    return (
      <Card data-testid="ocr-documents-empty">
        <CardContent className="py-10">
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <AlertCircle className="w-8 h-8" />
            <p className="text-sm font-medium">Aucun document OCR</p>
            <p className="text-xs">Les documents scannes du véhicule et du chauffeur apparaitront ici.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="ocr-documents-section">
      {/* Vehicle docs */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Truck className="w-4 h-4 text-amber-600" />
            Documents véhicule
            <Badge variant="secondary" className="text-[10px] ml-auto">{vehicleDocs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {vehicleDocs.length > 0 ? (
            <div className="space-y-2">
              {vehicleDocs.map(doc => <DocCard key={doc.id} doc={doc} />)}
            </div>
          ) : <EmptyState label="Aucun document vehicule scanne" />}
        </CardContent>
      </Card>

      {/* Driver docs */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-blue-600" />
            Documents chauffeur
            <Badge variant="secondary" className="text-[10px] ml-auto">{driverDocs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {driverDocs.length > 0 ? (
            <div className="space-y-2">
              {driverDocs.map(doc => <DocCard key={doc.id} doc={doc} />)}
            </div>
          ) : <EmptyState label="Aucun document chauffeur scanne" />}
        </CardContent>
      </Card>
    </div>
  );
};

export default OperationOCRDocuments;
